import { inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, exhaustMap, filter, merge, Observable, tap } from 'rxjs';

import { CaseService } from '../services/case.service';
import { DealershipCaseStatus } from '../../shared/enums/dealearship-case-status.enum';
import { BatteryStatus } from '../../shared/models/battery-status.model';
import { DealerStore } from './dealer.store';
import { ScreenStateStore } from './screen-state.store';

const STORAGE_KEY = 'batteryStatuses';

@Injectable()
export class BatteryStatusStore {
  private readonly casesService = inject(CaseService);
  private readonly dealerStore = inject(DealerStore);
  private readonly screenStateStore = inject(ScreenStateStore);

  private readonly _statuses = signal<Record<number, BatteryStatus>>(this.readFromStorage());

  readonly statuses = this._statuses.asReadonly();

  constructor() {
    merge(
      toObservable(this.dealerStore.selectedDealer),
      this.screenStateStore.polling$,
      this.screenStateStore.manualRefresh$,
    )
      .pipe(
        filter(() => this.screenStateStore.activeAgGridTab() === DealershipCaseStatus.OPEN),
        exhaustMap(() => this.fetchStatuses()),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private fetchStatuses(): Observable<Record<number, BatteryStatus>> {
    const selectedDealer = untracked(this.dealerStore.selectedDealer);
    if (!selectedDealer) {
      return EMPTY;
    }

    return this.casesService.getOpenCasesBatteryStatuses(selectedDealer.bpId).pipe(
      filter((statuses) => Object.keys(statuses).length > 0),
      tap((statuses) => this.setStatuses(statuses)),
      catchError(() => EMPTY),
    );
  }

  private setStatuses(statuses: Record<number, BatteryStatus>): void {
    this._statuses.set(statuses);
    this.writeToStorage(statuses);
  }

  private readFromStorage(): Record<number, BatteryStatus> {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Record<number, BatteryStatus>) : {};
    } catch {
      return {};
    }
  }

  private writeToStorage(statuses: Record<number, BatteryStatus>): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(statuses));
    } catch {
      // Ignore storage errors (e.g. private mode or quota exceeded).
    }
  }
}

